import { supabase } from '../supabase';

// Phase C.8 — ATHLETE CARD FOUNDATION. Seasonal Athlete Card photos
// (050). The bucket is PRIVATE — no object here is ever reachable by a
// bare/public URL; every read goes through a short-lived signed URL the
// authenticated client requests, gated by storage.objects' own RLS
// (050), which independently re-checks guardian/team-staff/platform-
// admin authority AND athlete_photos.can_display_in_app. The database
// row is the source of truth for WHICH photo is active; the signed URL
// is never stored, only requested fresh each time it's needed.

const BUCKET = 'athlete-photos';
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour — long enough for one page view, never persisted

/** The athlete's currently-active Card photo for one specific season, or
 * null if none has been uploaded yet — never a fallback to some OTHER
 * season's photo. Readable under athlete_photos' own RLS (050): a
 * guardian of this athlete, team staff of a team this athlete is
 * actively rostered on, or platform admin. */
export async function getAthleteCardPhoto(athleteId, seasonId) {
  if (!athleteId || !seasonId) return null;
  const { data, error } = await supabase
    .from('athlete_photos')
    .select('*')
    .eq('athlete_id', athleteId)
    .eq('season_id', seasonId)
    .eq('status', 'active')
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Resolves a storage_path to a short-lived signed URL for display —
 * never a permanent public URL, and never cached/stored anywhere beyond
 * this one call's return value. Fails (throws) if the caller isn't
 * actually authorized to read this exact object, per storage.objects'
 * own RLS (050) — this is the real enforcement point, not a convenience
 * wrapper being trusted on its own. */
export async function getAthleteCardPhotoUrl(storagePath) {
  if (!storagePath) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  return data?.signedUrl || null;
}

function extensionFor(file) {
  const fromName = /\.([a-zA-Z0-9]+)$/.exec(file?.name || '')?.[1];
  if (fromName) return fromName.toLowerCase();
  const fromType = /^image\/([a-zA-Z0-9]+)$/.exec(file?.type || '')?.[1];
  return (fromType || 'jpg').toLowerCase();
}

/** Guardian-only (server-independently-enforced by BOTH the Storage
 * upload policy and add_athlete_card_photo() itself, 050 — this
 * function does not decide authorization, it just calls the two steps
 * in the required order). Uploads the raw file to the athlete's private
 * '{athleteId}/{seasonId}/{uuid}.{ext}' path, then registers/replaces
 * the canonical athlete_photos row for that exact (athlete, season) —
 * archiving whatever was active before, never deleting it. Returns the
 * new athlete_photos row.
 *
 * PRE-APPLY FIX — best-effort cleanup. Storage upload happens BEFORE
 * the DB RPC (unchanged) — if the RPC then fails for any reason (e.g.
 * the season-membership check added alongside this fix), the object
 * that already landed in private Storage would otherwise be orphaned
 * (unreferenced by any athlete_photos row, but still consuming private
 * storage). On an RPC failure only, this makes ONE best-effort attempt
 * to remove that exact object before re-throwing. This is deliberately
 * NOT a reconciliation system — no retry, no queue, no background job,
 * no record of what was cleaned up or not. A cleanup failure is
 * swallowed (never allowed to replace or mask the original RPC error,
 * which is what the caller actually needs to see and what determines
 * whether the upload succeeded); a rare surviving orphan from a failed
 * cleanup attempt carries the same characteristics already accepted in
 * the C.8 review — private, unreferenced, never publicly reachable. */
export async function uploadAthleteCardPhoto({ athleteId, seasonId, file, capturedAt }) {
  const ext = extensionFor(file);
  const path = `${athleteId}/${seasonId}/${crypto.randomUUID()}.${ext}`;

  const { error: uploadError } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file?.type || undefined,
    upsert: false,
  });
  if (uploadError) throw uploadError;

  const { data, error } = await supabase.rpc('add_athlete_card_photo', {
    p_athlete_id: athleteId,
    p_season_id: seasonId,
    p_storage_path: path,
    p_captured_at: capturedAt || null,
  });
  if (error) {
    // Best-effort cleanup only. Supabase Storage's own remove() resolves
    // with { error } rather than rejecting on an ordinary failure (a
    // genuine network/runtime throw is also possible) — either way, a
    // cleanup problem is swallowed here and must never hide or replace
    // the ORIGINAL RPC error re-thrown below, which is what the caller
    // and the guardian-facing UI actually need to see.
    try {
      await supabase.storage.from(BUCKET).remove([path]);
    } catch {
      // swallowed — see above
    }
    throw error;
  }
  return data;
}

/** Guardian-only (server-independently-enforced by remove_athlete_card_
 * photo() itself, 051). ARCHIVES the current active athlete_photos row
 * for this exact (athlete, season) — the Card's photo, not the
 * underlying Storage object, which this never touches. A guardian can
 * upload a new Season photo again afterward at any time. Returns the
 * archived row, or null if there was no active photo to remove
 * (a harmless no-op, never an error). */
export async function removeAthleteCardPhoto({ athleteId, seasonId }) {
  const { data, error } = await supabase.rpc('remove_athlete_card_photo', {
    p_athlete_id: athleteId,
    p_season_id: seasonId,
  });
  if (error) throw error;
  return data;
}
